import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
  }
};

// Hamburger Menu Icon
const HamburgerIcon = ({ isOpen }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {isOpen ? (
      <>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </>
    ) : (
      <>
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
      </>
    )}
  </svg>
);

// Navigation Component
const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 w-full z-50 backdrop-blur-md border-b border-truvio-border bg-truvio-bg/80">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 flex items-center justify-between py-4">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text flex-shrink-0">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <span className="pill-tag">In development</span>
          <Link 
            to="/manifesto" 
            className="text-truvio-accent text-sm flex items-center gap-1 whitespace-nowrap"
          >
            Read our manifesto <ArrowRight className="w-3 h-3" />
          </Link>
        </div>

        {/* Mobile hamburger button */}
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-truvio-text p-2"
          aria-label="Toggle menu"
        >
          <HamburgerIcon isOpen={mobileMenuOpen} />
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-truvio-border bg-truvio-surface">
          <div className="max-w-[1200px] mx-auto px-6 py-4 flex flex-col gap-4">
            <span className="pill-tag w-fit">In development</span>
            <Link 
              to="/manifesto" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-truvio-accent text-sm flex items-center gap-1"
            >
              Read our manifesto <ArrowRight className="w-3 h-3" />
            </Link>
            <Link 
              to="/" 
              onClick={() => setMobileMenuOpen(false)}
              className="text-truvio-muted hover:text-truvio-text text-sm"
            >
              Back to home
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

// Section Component
const Section = ({ id, title, children }) => (
  <motion.section 
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    variants={fadeUp}
    id={id}
    className="mb-12 md:mb-16 scroll-mt-24"
  >
    <h2 className="font-playfair font-black text-xl md:text-2xl text-truvio-text mb-4 md:mb-6 leading-tight">
      {title}
    </h2>
    <div className="prose-privacy text-truvio-muted leading-relaxed space-y-4">
      {children}
    </div>
  </motion.section>
);

// Footer Component
const Footer = () => (
  <footer className="py-10 md:py-16 border-t border-truvio-border">
    <div className="max-w-[1200px] mx-auto px-6 md:px-12">
      <div className="flex flex-col gap-8 md:flex-row md:justify-between md:items-center md:gap-8">
        <Link to="/" className="font-playfair text-xl md:text-2xl font-black text-truvio-text">
          Truv<span className="italic text-truvio-primary">io</span>
        </Link>
        
        <nav className="flex items-center gap-8 md:gap-10 text-xs md:text-sm">
          <Link to="/manifesto" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300">
            Manifesto
          </Link>
          <Link to="/privacy" className="text-truvio-accent transition-colors duration-300">
            Privacy
          </Link>
          <a href="mailto:hello@truvio.me" className="text-truvio-muted hover:text-truvio-text transition-colors duration-300">
            Contact
          </a>
        </nav>

        <div className="text-left md:text-right">
          <p className="text-truvio-muted/70 text-[0.6875rem] md:text-xs tracking-wide">
            © 2026 Truvio · Registered in Cyprus · Data stored in the EU
          </p>
          <p className="text-truvio-muted/40 text-[0.625rem] mt-1.5 italic tracking-wide">
            truvio.me
          </p>
        </div>
      </div>
    </div>
  </footer>
);

// Main Privacy Page
function PrivacyPage() {
  useEffect(() => {
    document.title = "Privacy Policy — Truvio";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-truvio-bg min-h-screen relative">
      {/* Ambient glows */}
      <div className="glow-green"></div>
      <div className="glow-lime"></div>
      
      {/* Grain overlay */}
      <div className="grain-overlay"></div>
      
      {/* Content */}
      <div className="relative z-10">
        <Navigation />
        
        {/* Header */}
        <header className="pt-32 md:pt-40 pb-12 md:pb-16">
          <div className="max-w-[760px] mx-auto px-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-truvio-muted mb-6 block">
                Legal
              </span>
              <h1 className="font-playfair font-black text-3xl md:text-4xl lg:text-5xl text-truvio-text leading-[1.1] mb-6">
                Privacy Policy
              </h1>
              <p className="text-truvio-muted text-sm">
                Last updated: March 2026
              </p>
            </motion.div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-[760px] mx-auto px-6 pb-20">
          
          <Section id="who-we-are" title="Who we are">
            <p>
              Truvio is{'\u00A0'}a{'\u00A0'}verified review platform registered in{'\u00A0'}Cyprus and operating under European Union law. 
              We are committed to{'\u00A0'}protecting your privacy and ensuring that your personal data is{'\u00A0'}handled 
              responsibly and transparently.
            </p>
            <p>
              All data collected by{'\u00A0'}Truvio is{'\u00A0'}stored exclusively within the European Union, in{'\u00A0'}compliance 
              with GDPR and other applicable EU data protection regulations.
            </p>
          </Section>

          <Section id="data-we-collect" title="What data we collect">
            <p>We collect the following types of{'\u00A0'}data:</p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Email address:</strong> Collected when you sign up for our waitlist or{'\u00A0'}create an{'\u00A0'}account.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Company name:</strong> Collected if{'\u00A0'}you register as{'\u00A0'}a{'\u00A0'}business.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Usage data:</strong> Anonymous analytics data about how you interact with our website, collected to{'\u00A0'}improve our platform.</span>
              </li>
            </ul>
          </Section>

          <Section id="why-we-collect" title="Why we collect it">
            <p>We collect your data for the following purposes:</p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>To{'\u00A0'}notify you when Truvio launches and provide updates about the platform.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>To{'\u00A0'}communicate with you regarding your waitlist registration or{'\u00A0'}account.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>To{'\u00A0'}improve our platform based on{'\u00A0'}anonymous usage patterns and analytics.</span>
              </li>
            </ul>
          </Section>

          <Section id="legal-basis" title="Legal basis">
            <p>
              We process your personal data in{'\u00A0'}accordance with GDPR Article{'\u00A0'}6, based on{'\u00A0'}the following legal grounds:
            </p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Consent:</strong> When you voluntarily provide your email address to{'\u00A0'}join our waitlist or{'\u00A0'}sign up for our services.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Legitimate interest:</strong> To{'\u00A0'}analyse anonymous usage data and improve our platform, and to{'\u00A0'}communicate important updates about our services.</span>
              </li>
            </ul>
          </Section>

          <Section id="data-storage" title="Data storage">
            <p>
              Your data is{'\u00A0'}stored securely within the European Union using industry-standard encryption 
              and security measures.
            </p>
            <p className="mt-4">
              <strong className="text-truvio-text">We make the following commitments:</strong>
            </p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>Your data is{'\u00A0'}never sold to{'\u00A0'}third parties.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>Your data is{'\u00A0'}never shared with third parties except essential service providers necessary for platform operation (e.g., hosting, email delivery).</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>All service providers we work with are GDPR-compliant and bound by{'\u00A0'}data processing agreements.</span>
              </li>
            </ul>
          </Section>

          <Section id="reviewer-identity" title="Reviewer identity">
            <p>
              Protecting reviewer identity is{'\u00A0'}fundamental to{'\u00A0'}our platform. Reviewer identity information is:
            </p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>Stored under strict GDPR controls with enhanced security measures.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>Never disclosed to{'\u00A0'}businesses, regardless of{'\u00A0'}any payment or{'\u00A0'}subscription tier.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span>Disclosed only by{'\u00A0'}court order from a{'\u00A0'}competent EU jurisdiction{'\u00A0'}— this is{'\u00A0'}the only exception.</span>
              </li>
            </ul>
            <p className="mt-4 text-truvio-text/80 italic">
              This protection is{'\u00A0'}not a{'\u00A0'}feature{'\u00A0'}— it{'\u00A0'}is{'\u00A0'}an{'\u00A0'}architectural constraint we have placed on{'\u00A0'}ourselves.
            </p>
          </Section>

          <Section id="your-rights" title="Your rights">
            <p>
              Under GDPR, you have the following rights regarding your personal data:
            </p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Right of{'\u00A0'}access:</strong> Request a{'\u00A0'}copy of{'\u00A0'}the personal data we hold about you.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Right to{'\u00A0'}rectification:</strong> Request correction of{'\u00A0'}inaccurate or{'\u00A0'}incomplete data.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Right to{'\u00A0'}erasure:</strong> Request deletion of{'\u00A0'}your personal data ("right to{'\u00A0'}be{'\u00A0'}forgotten").</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Right to{'\u00A0'}data portability:</strong> Request transfer of{'\u00A0'}your data to{'\u00A0'}another service.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Right to{'\u00A0'}object:</strong> Object to{'\u00A0'}processing of{'\u00A0'}your data for certain purposes.</span>
              </li>
            </ul>
            <p className="mt-6">
              To{'\u00A0'}exercise any of{'\u00A0'}these rights, please contact us at{" "}
              <a href="mailto:privacy@truvio.me" className="text-truvio-accent hover:underline">
                privacy@truvio.me
              </a>
            </p>
          </Section>

          <Section id="cookies" title="Cookies">
            <p>
              We use minimal cookies on{'\u00A0'}our website, limited to:
            </p>
            <ul className="list-none space-y-3 mt-4">
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Essential cookies:</strong> Required for basic website functionality.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-truvio-primary mt-1">—</span>
                <span><strong className="text-truvio-text">Analytics cookies:</strong> Used to{'\u00A0'}collect anonymous usage data to{'\u00A0'}improve our platform.</span>
              </li>
            </ul>
            <p className="mt-4">
              We do not use advertising cookies or{'\u00A0'}share cookie data with third-party advertisers.
            </p>
          </Section>

          <Section id="contact" title="Contact">
            <p>
              For any privacy-related questions, concerns, or{'\u00A0'}requests, please contact us:
            </p>
            <div className="mt-6 bg-truvio-surface border border-truvio-border p-6 md:p-8">
              <p className="text-truvio-text mb-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:privacy@truvio.me" className="text-truvio-accent hover:underline">
                  privacy@truvio.me
                </a>
              </p>
              <p className="text-truvio-text">
                <strong>Data Controller:</strong> Truvio (Cyprus)
              </p>
            </div>
          </Section>

          {/* Back to Home CTA */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="mt-16 pt-10 border-t border-truvio-border text-center"
          >
            <Link to="/" className="btn-primary inline-flex items-center gap-2">
              Back to home <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default PrivacyPage;
